// What's new — the patch notes shown once to every admin who opens the
// dashboard after a release (owner, 2026-10-08: « a pop-up showing the new
// features and how to use them, clean and simple to understand »).
//
// ADD A RELEASE: put it FIRST in PATCH_NOTES with a new `id`. Everyone who has
// not seen that id gets the pop-up once. Max 10 items a release (owner 10-15:
// two big days in one patch) — short words, one « how » list each. `visual` names a mock in components/admin/WhatsNew.tsx;
// `image` (a /public path or URL) shows a real screenshot instead.

export type PatchVisual = 'preview-approval' | 'address-ask' | 'not-interested' | 'photo-gap' | 'whatsapp-line'
  | 'live-map' | 'delivery-board' | 'ai-captions' | 'reply-alert' | 'fixes'
  | 'print-gate' | 'complaint' | 'send-status' | 'server-regen' | 'fixes-1015'
  | 'cover-text' | 'agent-react' | 'agent-actions' | 'playbooks' | 'fixes-1030';

export interface PatchItem {
  title: string;
  /** One or two plain sentences: what changes for you. */
  what: string;
  /** Numbered steps. Empty = nothing to do, it works by itself. */
  how: string[];
  visual?: PatchVisual;
  image?: string;
  /** Several screenshots, shown one under the other (e.g. « before / blocked »). */
  images?: string[];
  /** « Désactivé par défaut » and the like. */
  badge?: string;
}

export interface PatchRelease {
  id: string;
  date: string;
  title: string;
  items: PatchItem[];
}

export const PATCH_NOTES: PatchRelease[] = [
  {
    // Owner, 2026-10-30: « the new patch notif, simple to understand what to
    // expect, not too much text ».
    id: '2026-10-30',
    date: '30 octobre 2026',
    title: 'Ce qui change',
    items: [
      {
        title: 'Du texte libre sur la couverture',
        what: 'Écrivez ce que vous voulez sur la face, le dos ou la tranche. Agrandissez, réduisez, changez la largeur et la hauteur. Le cadre pointillé montre ce qui ne sera pas coupé.',
        how: [
          'Couverture → « + Texte » → Face, Dos ou Tranche.',
          'Tirez les poignées. A− / A+ pour la taille.',
          'Cadre rouge ? « Mettre dans la zone sûre ».',
        ],
        visual: 'cover-text',
      },
      {
        title: 'L’agent répond mieux et plus vite',
        what: 'Réponse en quelques secondes, dans la langue de la cliente. Une commande du site reçoit un récapitulatif clair. Une plainte passe avant toute vente. Un simple « merci » reçoit juste un ❤️.',
        how: [],
        visual: 'agent-react',
      },
      {
        title: 'L’agent agit seul — vous pouvez annuler',
        what: 'Il envoie le lien de l’éditeur ou l’aperçu 3D, modifie l’album, retire la page de fin. Tout est noté avec un bouton Annuler. L’argent (remise, remboursement, plus de 100 DH) attend votre OK.',
        how: [
          'Assistant → « Validations ».',
          '« Annuler » remet l’album comme avant.',
          'Une question d’argent : « Valider » ou « Refuser ».',
        ],
        visual: 'agent-actions',
      },
      {
        title: 'L’agent apprend de vos bonnes conversations',
        what: 'Chaque dimanche, il propose des réponses tirées des conversations qui ont bien fini. Il n’utilise que celles que vous validez.',
        how: [
          'Assistant → « Validations » → « Playbooks à valider ».',
          '« Valider », « Modifier » ou « Refuser ».',
        ],
        visual: 'playbooks',
      },
      {
        title: 'Corrigé aujourd’hui',
        what: 'Les problèmes signalés aujourd’hui sont réglés.',
        how: [],
        visual: 'fixes-1030',
      },
    ],
  },
  {
    // Owner, 2026-10-15: « today's and yesterday's big changes in a new patch,
    // explained simply so each user understands, with illustrations and
    // screenshots of the automations and the new features ». Screenshots in
    // public/patch/2026-10-15 were taken from local previews with invented data.
    id: '2026-10-15',
    date: '14 et 15 octobre 2026',
    title: 'Nouveautés des deux derniers jours',
    items: [
      {
        title: 'Le parcours de l’agent, sur une seule page',
        badge: 'Nouveau — l’agent ne le suit pas encore',
        what: 'Toutes les étapes de l’agent, dans l’ordre : accueil, photos, aperçu, avance, impression… Vous changez l’ordre, vous allumez ou éteignez une étape, vous réglez les relances. Un ordre impossible devient rouge et ne peut pas être enregistré. Pour l’instant, rien ne change pour les clientes.',
        how: [
          'Assistant → Automations → « Processus de l’agent ».',
          'Glissez une étape (ou ▲ ▼) et touchez ON / OFF.',
          '« Enregistrer la version », puis « Activer » : seules les nouvelles conversations la suivront.',
          'Une erreur ? « Historique des versions » → « Revenir à cette version ».',
        ],
        image: '/patch/2026-10-15/processus.jpg',
      },
      {
        title: 'Les consignes de chaque étape',
        badge: 'Change l’agent tout de suite',
        what: 'Touchez une étape : vous voyez les règles du Cerveau qui la concernent. Modifiez-les, ou écrivez une nouvelle consigne : l’IA la mélange aux autres règles, et refuse si elle en contredit une.',
        how: [
          'Touchez une étape → « Instructions de cette étape ».',
          '« Proposer automatiquement » : l’IA trouve les règles de l’étape.',
          '« Ajouter une instruction » → « Vérifier et fusionner » : l’ancien texte est barré, le nouveau en vert.',
          '« Appliquer au Cerveau ». Une contradiction en rouge bloque le bouton : reformulez.',
        ],
        images: ['/patch/2026-10-15/etape-fusion.jpg', '/patch/2026-10-15/etape-contradiction.jpg'],
      },
      {
        title: 'Pas d’impression sans l’avance',
        what: 'Une commande ne passe plus en « Prêt à imprimer », « En production » ou « Imprimé » tant que l’avance n’est pas reçue — depuis la page commande, la liste, WhatsApp ou une reprise. Un admin peut forcer, avec une raison gardée sur la commande.',
        how: [
          'Statut refusé ? Vérifiez que le paiement est bien enregistré.',
          'Vraiment nécessaire : un admin répond à « Forcer sans avance ? » avec la raison.',
        ],
        visual: 'print-gate',
      },
      {
        title: 'Une plainte met l’agent en pause',
        what: 'Quand une cliente se plaint, l’agent s’excuse une fois puis se tait : plus de relance ni de message d’étape. La plainte reste ouverte jusqu’à ce qu’une personne la ferme, et vous êtes rappelé toutes les 48 h.',
        how: [
          'Répondez vous-même à la cliente.',
          'Une fois réglé : « Plainte résolue » (barre de la conversation ou fiche commande). L’agent reprend.',
        ],
        visual: 'complaint',
      },
      {
        title: 'Vous savez quand un message part',
        what: '« Approuver et envoyer » dit maintenant ce qui va se passer : part maintenant, part à 08:00 (heure calme), attend les pages, ou bloqué — avec la raison. Un envoi qui échoue, est retenu ou en retard fait sonner la cloche.',
        how: [
          'Après « Approuver », lisez la ligne sous le bouton.',
          'Messages programmés : la liste montre 7 jours, avec l’état de chacun.',
        ],
        visual: 'send-status',
      },
      {
        title: '« Régénérer le PDF » se fait sur le serveur',
        what: 'Le PDF et les pages 3D sont refaits par le serveur : plus besoin de garder la fenêtre ouverte. Le travail démarre en moins d’une minute. La bannière « version précédente » n’apparaît plus que sur l’album vraiment en retard.',
        how: ['Éditeur (admin) ou page commande → « Régénérer ».', 'Vous pouvez fermer l’onglet tout de suite.'],
        visual: 'server-regen',
      },
      {
        title: 'Légendes IA, refaites',
        what: 'Une légende par page, écrite en regardant toutes ses photos (elle remplace Sous-titre et Section). Une case pour ce que la cliente demande : ses mots entre « guillemets » sont recopiés tels quels. Les polices se voient avant de choisir, et « Tout retirer » enlève tout d’un clic.',
        how: [
          'Éditeur → « Légendes IA ».',
          'Choisissez la langue et écrivez la demande de la cliente.',
          '« Générer les légendes », puis « Refaire » ou « Retirer » page par page.',
          '« Appliquer ».',
        ],
        image: '/patch/2026-10-15/legendes.jpg',
      },
      {
        title: 'Thèmes : des pages décorées avec ses photos',
        badge: 'Actif pour le thème Voyage',
        what: 'Un album à thème reçoit des pages décorées avec ses propres photos. Chaque album a sa barre « Thème de l’album » : thème, coloris, couleur, % de pages avec modèle, nombre de modèles différents. Chaque changement s’enregistre tout seul.',
        how: [
          'Page commande ou éditeur → barre « Thème de l’album ».',
          'Éditeur → « Modèles » : touchez un modèle pour l’appliquer à la page.',
          'Éditeur → Éléments → « Stickers » : 267 décorations.',
        ],
        image: '/patch/2026-10-15/theme-bar.jpg',
      },
      {
        title: 'Demander le paiement en un bouton',
        what: 'Le message de paiement est préparé depuis la commande : récapitulatif, avance de 100 DH par album, reste à la livraison, RIB. Les montants et le RIB sont verrouillés ; vous ne modifiez que le début et la fin. Une option cochée sur la commande s’ajoute au total tout de suite.',
        how: [
          'Conversation ou page commande → « 💰 Demander le paiement ».',
          'Lisez les avertissements en jaune, puis « Envoyer ».',
          'Options : page commande → « Options de la commande » → cochez.',
        ],
        image: '/patch/2026-10-15/paiement.jpg',
      },
      {
        title: 'Corrigé',
        what: 'Une étiquette AMEEX = une page, sans page blanche. La bannière « version précédente » ne reste plus après un enregistrement sans changement. La touche Supprimer efface l’élément choisi dans l’éditeur. Les livres à thème s’ouvrent comme les autres.',
        how: [],
        visual: 'fixes-1015',
      },
    ],
  },
  {
    // Owner, 2026-10-10 evening: « simple to understand in seconds, with an
    // illustration of what we added tonight ». One sentence per card.
    id: '2026-10-10-soir',
    date: '10 octobre 2026 · soir',
    title: 'Nouveautés de ce soir',
    items: [
      {
        title: 'Live : qui avance, qui bloque',
        what: 'Chaque cliente est une pastille qui glisse d’étape en étape. Rouge = bloquée chez nous, jaune = attend votre décision.',
        how: ['Assistant → Live.', 'Touchez une pastille pour voir pourquoi l’agent a agi.'],
        visual: 'live-map',
      },
      {
        title: 'Livraison AMEEX : on regarde, on ne clique plus',
        what: 'Quatre chiffres, des colis qui avancent tout seuls, et une carte pour chaque colis. Les problèmes sont en rouge, en haut.',
        how: ['Menu → Livraison AMEEX.', 'Touchez un colis : carte, historique, livreur, WhatsApp client.'],
        visual: 'delivery-board',
      },
      {
        title: 'Légendes IA, avec une citation',
        what: 'Une phrase sous chaque photo, écrite en regardant la photo, plus une citation si vous voulez. Elle ne s’arrête plus quand le budget IA du jour est atteint.',
        how: ['Éditeur → « Légendes IA ».', 'Cochez « + une citation » et « Cadre de la couleur du livre ».', 'Relisez, puis « Appliquer ».'],
        visual: 'ai-captions',
      },
      {
        title: 'Répondez à l’alerte, c’est tout',
        what: 'Écrivez comme vous parlez : l’agent le remet au propre dans la langue de la cliente, puis l’envoie après 1 minute.',
        how: ['Glissez l’alerte vers la droite.', 'Écrivez votre réponse.', 'STOP pour annuler.'],
        visual: 'reply-alert',
      },
      {
        title: 'Corrigé ce soir',
        what: 'Les vocaux et les vidéos se lisent jusqu’au bout, plus aucun message n’est envoyé deux fois, et le PDF ne bloque plus sur une photo mal mesurée.',
        how: [],
        visual: 'fixes',
      },
    ],
  },
  {
    id: '2026-10-10',
    date: '10 octobre 2026',
    title: 'Nouveautés',
    items: [
      {
        title: 'Les alertes WhatsApp : seulement l’essentiel',
        badge: 'Réponses sûres : actif · Tri des alertes : à activer',
        what: 'Vos réponses aux alertes sont sûres : OK, أوك, wakha ou ✅ approuvent, NON refuse, et une réponse à un paiement ne part jamais chez la cliente. Un texte pour une cliente attend 1 minute (STOP pour annuler). Quand Omar agit, vous recevez « Fait par Omar ». Activé, le tri n’envoie sur WhatsApp que 6 cas, plus un résumé à 9 h et 18 h.',
        how: [
          'Répondez en citant l’alerte (glissez le message vers la droite).',
          'Un changement de statut (« #1217 livré ») vous demande d’abord OK.',
          'Le tri : Assistant → Réglages → « Alertes : seulement ce qui demande une action ».',
        ],
      },
      {
        title: 'Le prix des pages en plus, clair avant l’aperçu',
        badge: 'Désactivé — à activer',
        what: 'Chaque commande garde son propre prix de page supplémentaire : les anciennes restent à 8 DH, les nouvelles passent à 5 DH quand vous l’activez. Au-delà de 36 pages, l’aperçu part avec le total exact et propose 36 pages au prix du pack.',
        how: [
          'Assistant → Réglages → « Page supplémentaire à 5 DH (nouvelles commandes) ».',
          'Assistant → Réglages → « Annoncer le prix avec l’aperçu ».',
          'La cliente répond « 36 » : l’album est refait en 36 pages et le nouvel aperçu part tout seul.',
          'Le total change après son accord : la demande d’avance et le colis AMEEX sont bloqués, et vous êtes prévenu.',
        ],
      },
      {
        title: 'Plus de messages faux aux clientes',
        badge: 'Désactivé — à activer',
        what: '« Photos bien reçues », « on attend vos photos », « votre album est prêt » et « une personne va vous répondre » sont vérifiés avant de partir. Si ce n’est pas vrai, rien ne part et vous recevez l’alerte avec la raison.',
        how: ['Assistant → Réglages → « Bloquer les messages faux sur les photos et l’aperçu ».'],
      },
      {
        title: 'Qui nous attend',
        badge: 'Liste : active · Alertes : à activer',
        what: 'Sur le tableau de bord, les clientes qui attendent après nous, la plus ancienne en premier : photos reçues sans aperçu, ou passée à une personne sans réponse.',
        how: [
          'Tableau de bord → « Qui nous attend » → touchez une cliente pour ouvrir sa conversation.',
          'Être alerté (24 h sans aperçu, 3 h sans réponse) : Assistant → Réglages → « Alerter quand une cliente nous attend ».',
        ],
      },
      {
        title: 'Commandes et conversations',
        what: 'Une commande WhatsApp naît seulement avec des photos ou un paiement — avant, tout reste sur la conversation. Dans une conversation, vous pouvez programmer un message, et une cliente qui revient montre toutes ses commandes.',
        how: [
          'Programmer : touchez 🕘 près de « Envoyer », puis demain matin, ce soir ou une date.',
          'Plusieurs commandes : choisissez la bonne dans la liste de la conversation.',
          'La couleur du titre de la couverture suit maintenant celle de la tranche.',
        ],
      },
    ],
  },
  {
    id: '2026-10-08',
    date: '8 octobre 2026',
    title: 'Nouveautés',
    items: [
      {
        title: 'Vous validez chaque aperçu 3D avant qu’il parte',
        badge: 'Désactivé — à activer',
        what: 'L’album est d’abord contrôlé (photos dans l’ordre d’envoi, aucun visage coupé, consignes de la cliente appliquées). Ensuite vous recevez un WhatsApp. Rien ne part chez la cliente avant votre OK.',
        how: [
          'Activez-le : Assistant → Réglages → « Approuver chaque aperçu 3D avant qu’il parte ».',
          'Répondez OK au message WhatsApp (ou « OK 1205 » avec le numéro de commande).',
          'Ou cliquez « Approuver et envoyer » dans l’Inbox ou sur la page de la commande.',
          'Vous voulez changer quelque chose ? Répondez autre chose que OK : rien ne part, retouchez l’album, puis OK.',
        ],
        visual: 'preview-approval',
      },
      {
        title: 'L’adresse est demandée toute seule',
        what: 'Une commande validée à qui il manque le nom, la ville ou l’adresse complète reçoit la question automatiquement — deux fois au plus. Ensuite, vous êtes prévenu.',
        how: [],
        visual: 'address-ask',
      },
      {
        title: '« Plus intéressée » = plus aucune relance',
        what: 'Quand une cliente écrit qu’elle n’est plus intéressée, l’assistant arrête toutes les relances et vous prévient. C’est vous qui confirmez l’annulation.',
        how: [],
        visual: 'not-interested',
      },
      {
        title: 'L’espace blanc entre les photos est bien imprimé',
        what: 'Avant, « Régénérer » pouvait refaire le PDF sans l’espace entre les photos. C’est corrigé : l’éditeur, l’aperçu 3D et l’impression montrent le même espace.',
        how: ['Éditeur → réglage « Espace entre les photos » → Confirmer.'],
        visual: 'photo-gap',
      },
      {
        title: 'WhatsApp se reconnecte tout seul',
        what: 'Si la ligne plante mais reste connectée, elle redémarre seule — plus besoin de rescanner le QR. Et si WhatsApp bloque les envois, vous recevez une alerte sur le téléphone.',
        how: [],
        visual: 'whatsapp-line',
      },
    ],
  },
];

export const LATEST_PATCH_ID = PATCH_NOTES[0]?.id ?? null;
const SEEN_KEY = 'storiz.admin.patchNotesSeen';

/** The release this browser has not seen yet, or null. Never throws. */
export function unseenRelease(read: () => string | null = () => localStorage.getItem(SEEN_KEY)): PatchRelease | null {
  const latest = PATCH_NOTES[0];
  if (!latest) return null;
  try {
    return read() === latest.id ? null : latest;
  } catch {
    return null;   // storage blocked: never nag on every page
  }
}

export function markPatchSeen(id: string): void {
  try { localStorage.setItem(SEEN_KEY, id); } catch { /* per-browser convenience only */ }
}
